import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCart } from '../contexts/CartContext';
import { useFormatters } from '../hooks/useFormatters';
import { ShoppingBag, ChevronUp, ChevronDown, Trash2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

export default function StickyCartBar() {
  const { t } = useTranslation();
  const { items, total, itemCount, clearCart, removeItem, updateQuantity, venueCurrency } = useCart();
  const { formatCurrency } = useFormatters({ currency: venueCurrency });
  const [isExpanded, setIsExpanded] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const navigate = useNavigate();

  if (itemCount === 0) return null;

  const handleClearCart = () => {
    clearCart();
    setIsExpanded(false);
    setShowClearConfirm(false);
    toast.success(t('cart.toastCartCleared'), {
      icon: '🗑️',
      style: {
        background: '#27272a',
        color: '#fff',
        border: '1px solid #3f3f46',
      },
    });
  };

  const formatCustomizations = (customizations: Record<string, string> | undefined) => {
    if (!customizations || Object.keys(customizations).length === 0) return '';
    return ` (${Object.values(customizations).join(', ')})`;
  };

  const handleCheckout = () => {
    if (items.length === 0) {
      navigate('/');
      return;
    }
    navigate('/checkout');
  };

  return (
    <>
      {/* Add padding to prevent overlap with page content */}
      <div className="h-24" />

      {/* Sticky Cart Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-zinc-900 border-t border-zinc-800 shadow-lg z-40">
        {/* Clear Cart Confirmation Dialog */}
        {showClearConfirm && (
          <div className="absolute bottom-full left-0 right-0 p-4 bg-red-900/90 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <p className="text-white">{t('cart.clearConfirmPrompt')}</p>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleClearCart}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-500 transition-colors"
                >
                  {t('cart.clearCart')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Expanded Cart Preview */}
        {isExpanded && (
          <div className="max-h-64 overflow-y-auto border-t border-zinc-800">
            <div className="p-4 space-y-4">
              {items.map((item) => (
                <div key={`${item.id}-${JSON.stringify(item.customizations)}`} className="flex items-center justify-between bg-zinc-800 p-3 rounded-lg">
                  <div className="flex items-center space-x-4">
                    {item.image_url && (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-12 h-12 object-cover rounded-md"
                      />
                    )}
                    <div>
                      <h4 className="text-white font-medium">
                        {item.name}
                        <span className="text-gray-400">{formatCustomizations(item.customizations)}</span>
                      </h4>
                      <p className="text-sm text-gray-400">{t('cart.itemPriceEach', { price: formatCurrency(item.price) })}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="text-gray-400 hover:text-white transition-colors"
                      >
                        -
                      </button>
                      <span className="text-white">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="text-gray-400 hover:text-white transition-colors"
                      >
                        +
                      </button>
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="text-red-400 hover:text-red-300 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Main Cart Bar */}
        <div className="px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center text-white hover:text-yellow-400 transition-colors"
            >
              <ShoppingBag className="w-6 h-6 mr-2" />
              <span className="font-medium">{t('cart.itemsCount', { count: itemCount })}</span>
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 ml-2" />
              ) : (
                <ChevronUp className="w-5 h-5 ml-2" />
              )}
            </button>
            <button
              onClick={() => setShowClearConfirm(true)}
              className="text-red-400 hover:text-red-300 transition-colors"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center space-x-6">
            <div className="text-white">
              {t('cart.totalLabel')} <span className="text-yellow-400 font-semibold">{formatCurrency(total)}</span>
            </div>
            <button
              onClick={handleCheckout}
              className="px-6 py-2 bg-yellow-400 text-black rounded-md font-medium hover:bg-yellow-300 transition-colors"
            >
              {t('cart.checkout')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}